import { GRID_COLS, GRID_MAX_ROWS, TEARSHEET_PANELS } from '../constants';
import { GridLayoutItem, TearsheetPanelType, TearsheetV2Layout } from '../types';

export function generateNodeId(): string {
  return `panel-${crypto.randomUUID()}`;
}

// Check if a new item placement is valid (within bounds)
export function isValidPlacement(item: GridLayoutItem): boolean {
  const rightEdge = item.x + item.w;
  const bottomEdge = item.y + item.h;
  return item.x >= 0 && item.y >= 0 && rightEdge <= GRID_COLS && bottomEdge <= GRID_MAX_ROWS;
}

// Check if layout is valid (all items within bounds)
export function isValidLayout(items: GridLayoutItem[]): boolean {
  return items.every(isValidPlacement);
}

// Check if two items collide
function itemsCollide(a: GridLayoutItem, b: GridLayoutItem): boolean {
  return !(a.x + a.w <= b.x || b.x + b.w <= a.x || a.y + a.h <= b.y || b.y + b.h <= a.y);
}

// Find first available position for a new item
export function findAvailablePosition(
  layout: TearsheetV2Layout,
  w: number,
  h: number
): { x: number; y: number } | null {
  // Try each row from top to bottom
  for (let y = 0; y <= GRID_MAX_ROWS - h; y++) {
    // Try each column from left to right
    for (let x = 0; x <= GRID_COLS - w; x++) {
      const testItem: GridLayoutItem = {
        i: 'test',
        x,
        y,
        w,
        h,
        panelType: 'header',
      };

      // Check if this position collides with any existing item
      const hasCollision = layout.items.some((item) => itemsCollide(testItem, item));

      if (!hasCollision && isValidPlacement(testItem)) {
        return { x, y };
      }
    }
  }
  return null; // No space available
}

// Add a new panel to the layout
export function addItem(
  layout: TearsheetV2Layout,
  panelType: TearsheetPanelType,
  position?: { x: number; y: number },
  sizeOverride?: { w: number; h: number }
): TearsheetV2Layout {
  const defaultSize = TEARSHEET_PANELS[panelType].defaultSize;
  const w = sizeOverride?.w ?? defaultSize.w;
  const h = sizeOverride?.h ?? defaultSize.h;

  // Use provided position or find first available
  const pos = position ?? findAvailablePosition(layout, w, h);

  if (!pos) {
    // No space available
    return layout;
  }

  const newItem: GridLayoutItem = {
    i: generateNodeId(),
    x: pos.x,
    y: pos.y,
    w,
    h,
    panelType,
  };

  // Validate placement (bounds check)
  if (!isValidPlacement(newItem)) {
    return layout;
  }

  // Check for collisions with existing items
  const hasCollision = layout.items.some((item) => itemsCollide(newItem, item));
  if (hasCollision) {
    return layout;
  }

  return {
    ...layout,
    items: [...layout.items, newItem],
  };
}

// Remove a panel from the layout
export function removeItem(layout: TearsheetV2Layout, itemId: string): TearsheetV2Layout {
  return {
    ...layout,
    items: layout.items.filter((item) => item.i !== itemId),
  };
}

// Resize a panel in the layout
export function resizeItem(
  layout: TearsheetV2Layout,
  itemId: string,
  newW: number,
  newH: number
): TearsheetV2Layout {
  const itemIndex = layout.items.findIndex((item) => item.i === itemId);
  if (itemIndex === -1) {
    return layout;
  }

  const item = layout.items[itemIndex];
  const resizedItem: GridLayoutItem = {
    ...item,
    w: newW,
    h: newH,
  };

  // Validate bounds
  if (!isValidPlacement(resizedItem)) {
    return layout;
  }

  // Check for collisions with other items
  const hasCollision = layout.items.some(
    (other, idx) => idx !== itemIndex && itemsCollide(resizedItem, other)
  );
  if (hasCollision) {
    return layout;
  }

  const newItems = [...layout.items];
  newItems[itemIndex] = resizedItem;

  return {
    ...layout,
    items: newItems,
  };
}

// Move a panel to a new position
export function moveItem(
  layout: TearsheetV2Layout,
  itemId: string,
  newX: number,
  newY: number
): TearsheetV2Layout {
  const itemIndex = layout.items.findIndex((item) => item.i === itemId);
  if (itemIndex === -1) {
    return layout;
  }

  const item = layout.items[itemIndex];
  const movedItem: GridLayoutItem = {
    ...item,
    x: newX,
    y: newY,
  };

  // Validate bounds
  if (!isValidPlacement(movedItem)) {
    return layout;
  }

  // Check for collisions with other items (excluding self)
  const hasCollision = layout.items.some((other, idx) => idx !== itemIndex && itemsCollide(movedItem, other));
  if (hasCollision) {
    return layout;
  }

  const newItems = [...layout.items];
  newItems[itemIndex] = movedItem;

  return {
    ...layout,
    items: newItems,
  };
}

// Move and resize a panel in one atomic operation
export function moveAndResizeItem(
  layout: TearsheetV2Layout,
  itemId: string,
  newX: number,
  newY: number,
  newW: number,
  newH: number
): TearsheetV2Layout {
  const itemIndex = layout.items.findIndex((item) => item.i === itemId);
  if (itemIndex === -1) {
    return layout;
  }

  const item = layout.items[itemIndex];
  const updatedItem: GridLayoutItem = {
    ...item,
    x: newX,
    y: newY,
    w: newW,
    h: newH,
  };

  // Validate bounds
  if (!isValidPlacement(updatedItem)) {
    return layout;
  }

  // Check for collisions with other items (excluding self)
  const hasCollision = layout.items.some(
    (other, idx) => idx !== itemIndex && itemsCollide(updatedItem, other)
  );
  if (hasCollision) {
    return layout;
  }

  const newItems = [...layout.items];
  newItems[itemIndex] = updatedItem;

  return {
    ...layout,
    items: newItems,
  };
}

// Get all used panel types
export function getUsedPanelTypes(layout: TearsheetV2Layout): Set<TearsheetPanelType> {
  return new Set(layout.items.map((item) => item.panelType));
}

// Get item by ID
export function getItem(layout: TearsheetV2Layout, itemId: string): GridLayoutItem | undefined {
  return layout.items.find((item) => item.i === itemId);
}
